// The core build: no session recorder in the bundle, so replay cannot be switched on by config.
import mixpanel from 'mixpanel-browser/src/loaders/loader-module-core';

import { logger } from '../logger';

import type { AnalyticsEvents, AnalyticsSuperProperties } from './events';
import type { Config } from 'mixpanel-browser';

let started = false;

/**
 * Runs a call on the SDK only once it is started, and never lets it throw.
 *
 * Before `init` the SDK has no configuration, so a call could post somewhere other than
 * `/mpintake`; environments without the flag, E2E sessions and bots never start it. A throw is
 * swallowed for the reason `monitoring.trackAction` gives: these calls sit next to work whose
 * outcome they must not decide.
 */
const call = (name: string, run: () => unknown) => {
  if (!started) return;
  try {
    run();
  } catch (error) {
    logger.warn(`[analytics.${name}] could not record`, { error });
  }
};

/**
 * Product analytics: events for Mixpanel, sent to our own `/mpintake` and from there to Snowflake.
 * Separate from `monitoring`, which is Datadog's.
 *
 * Only `/track` reaches the ingest service. Profiles (`people`) and groups go to endpoints it does
 * not accept, and the SDK drops a batch that gets a 4xx, so they are not offered here.
 */
export const analytics = {
  init: (token: string, config: Partial<Config>) => {
    try {
      mixpanel.init(token, config);
      started = true;
    } catch (error) {
      logger.warn('[analytics.init] could not start', { error });
    }
  },
  track: <E extends keyof AnalyticsEvents>(event: E, properties: AnalyticsEvents[E]) =>
    call('track', () => mixpanel.track(event, properties)),
  identify: (userId: string) => call('identify', () => mixpanel.identify(userId)),
  reset: () => call('reset', () => mixpanel.reset()),
  register: <K extends keyof AnalyticsSuperProperties>(key: K, value: AnalyticsSuperProperties[K]) =>
    call('register', () => mixpanel.register({ [key]: value })),
  unregister: (key: keyof AnalyticsSuperProperties) => call('unregister', () => mixpanel.unregister(key)),
};
